import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Receipt, Download, Search, Filter, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Clock, CreditCard, AlertCircle, Building2,
  TrendingUp, IndianRupee, Hash, Calendar, X, Printer, FileText,
  Eye, ChevronDown, ArrowLeft, MessageSquare,
} from 'lucide-react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { useNavigate } from 'react-router-dom';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';
import API_BASE_URL from '../Config/config';

/* ── helpers ─────────────────────────────────────────────────── */
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtINR = (n) => `₹${fmt(parseFloat(n || 0).toFixed(0))}`;
const fmtDate = (d) => d
  ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';
const fmtDateShort = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

/* ── Online payment status config ────────────────────────────── */
const STATUS_META = {
  SUCCESS: { label: 'Success', cls: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500', Icon: CheckCircle2 },
  PENDING: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', Icon: Clock },
  FAILED:  { label: 'Failed',  cls: 'bg-red-50  text-red-700  border-red-200',   dot: 'bg-red-500',   Icon: XCircle },
};
const statusMeta = (s) => STATUS_META[(s || '').toUpperCase()] || STATUS_META.PENDING;

/* ── Enterprise status config ────────────────────────────────── */
const ENT_STATUS = {
  PENDING:                 { label: 'Pending',                color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', dot: '#F59E0B' },
  SUBMITTED:               { label: 'Submitted',              color: '#1E40AF', bg: '#EFF6FF', border: '#BFDBFE', dot: '#3B82F6' },
  RESUBMITTED:             { label: 'Resubmitted',            color: '#0369A1', bg: '#F0F9FF', border: '#BAE6FD', dot: '#0EA5E9' },
  UNDER_VERIFICATION:      { label: 'Under Verification',     color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', dot: '#F59E0B' },
  APPROVED:                { label: 'Approved',               color: '#065F46', bg: '#F0FDF4', border: '#BBF7D0', dot: '#10B981' },
  REJECTED:                { label: 'Rejected',               color: '#7F1D1D', bg: '#FEF2F2', border: '#FECACA', dot: '#EF4444' },
  CLARIFICATION_REQUESTED: { label: 'Clarification Needed',  color: '#7C2D12', bg: '#FFF7ED', border: '#FED7AA', dot: '#EA580C' },
  CONVERTED:               { label: 'Converted',              color: '#14532D', bg: '#F0FDF4', border: '#BBF7D0', dot: '#059669' },
};

const EntStatusBadge = ({ status }) => {
  const cfg = ENT_STATUS[status] || ENT_STATUS.PENDING;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '3px 9px', borderRadius: '99px',
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.dot }} />
      {cfg.label}
    </span>
  );
};

const PLAN_LABEL = { GOLD_LAUNCH: 'Gold Launch', PLATINUM_GROWTH: 'Platinum Growth', TITANIUM_ELITE: 'Titanium Elite' };
const planLabel = (code, name) => name || PLAN_LABEL[code] || code || '—';

/* ── Skeleton ─────────────────────────────────────────────────── */
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-100 rounded ${className}`} />
);

/* ── QR Code generator ─────────────────────────────────────────── */
async function generateQRDataURL(text) {
  try {
    const size = 200;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(10, 10, 40, 40); ctx.fillStyle = '#ffffff'; ctx.fillRect(14, 14, 32, 32); ctx.fillStyle = '#1a1a1a'; ctx.fillRect(18, 18, 24, 24);
    ctx.fillRect(150, 10, 40, 40); ctx.fillStyle = '#ffffff'; ctx.fillRect(154, 14, 32, 32); ctx.fillStyle = '#1a1a1a'; ctx.fillRect(158, 18, 24, 24);
    ctx.fillRect(10, 150, 40, 40); ctx.fillStyle = '#ffffff'; ctx.fillRect(14, 154, 32, 32); ctx.fillStyle = '#1a1a1a'; ctx.fillRect(18, 158, 24, 24);
    let hash = 0;
    for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
    const cellSize = 6, startX = 60, startY = 60, cols = 14, rows = 14;
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      if ((hash >> ((r * cols + c) % 32)) & 1) { ctx.fillStyle = '#1a1a1a'; ctx.fillRect(startX + c * cellSize, startY + r * cellSize, cellSize - 1, cellSize - 1); }
    }
    ctx.fillStyle = '#4F46E5'; ctx.font = 'bold 9px monospace'; ctx.textAlign = 'center'; ctx.fillText('VERIFIED', size / 2, size - 8);
    return canvas.toDataURL('image/png');
  } catch { return null; }
}

async function fetchImageAsBase64(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.onerror = () => resolve(null); r.readAsDataURL(blob); });
  } catch { return null; }
}

const EVOLVEX_LOGO_URL = 'https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/college_uploads/evolvex-logo.jpeg';

/* ── PDF Generator for Razorpay receipts ─────────────────────── */
async function generateReceiptPDF(receipt) {
  const { receipt_number, receipt_date, transaction: tx, college, company } = receipt;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logoBase64 = await fetchImageAsBase64(EVOLVEX_LOGO_URL);
  const W = 210, H = 297, margin = 14, contentW = W - margin * 2;
  const BRAND = [79, 70, 229], DARK = [17, 24, 39], MID = [75, 85, 99], LIGHT = [156, 163, 175], GREEN = [16, 185, 129], BG = [249, 250, 251];
  const setRGB = (r, g, b) => doc.setTextColor(r, g, b);
  const setFill = (r, g, b) => doc.setFillColor(r, g, b);
  const setDraw = (r, g, b) => doc.setDrawColor(r, g, b);
  const drawWatermark = () => {
    doc.saveGraphicsState();
    try { doc.setGState(new doc.GState({ opacity: 0.08 })); } catch (_) {}
    doc.setTextColor(180, 180, 180); doc.setFontSize(90); doc.setFont('helvetica', 'bold');
    doc.text('PAID', W / 2, H / 2, { angle: 45, align: 'center', baseline: 'middle' });
    doc.restoreGraphicsState(); doc.setTextColor(0, 0, 0);
  };
  drawWatermark();
  setFill(...BRAND); doc.rect(0, 0, W, 28, 'F');
  let textStartX = margin;
  if (logoBase64) { doc.addImage(logoBase64, 'JPEG', margin, 5, 18, 18); textStartX = margin + 22; }
  setRGB(255, 255, 255); doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.text('EvolveX Innovations', textStartX, 11);
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.text('Skill Genie — AI-Powered Learning Platform', textStartX, 17); doc.text(`GSTIN: ${company.gstin}`, textStartX, 22);
  doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.text('PAYMENT RECEIPT', W - margin, 13, { align: 'right' });
  doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.text(`Receipt No: ${receipt_number}`, W - margin, 20, { align: 'right' }); doc.text(`Date: ${fmtDateShort(receipt_date)}`, W - margin, 25, { align: 'right' });
  let y = 36;
  setFill(209, 250, 229); doc.roundedRect(margin, y, contentW, 10, 2, 2, 'F'); setRGB(...GREEN); doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.text('✔  PAYMENT SUCCESSFUL', margin + 4, y + 6.5); y += 16;
  const colW = (contentW - 6) / 2;
  const drawBox = (x, boxY, w, h, label, lines) => {
    setFill(...BG); setDraw(229, 231, 235); doc.setLineWidth(0.3); doc.roundedRect(x, boxY, w, h, 2, 2, 'FD');
    setRGB(...BRAND); doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.text(label.toUpperCase(), x + 4, boxY + 5); setDraw(229, 231, 235); doc.line(x + 4, boxY + 6.5, x + w - 4, boxY + 6.5);
    setRGB(...DARK); doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
    lines.forEach((line, i) => { if (line === null) return; const isBold = typeof line === 'object'; const text = isBold ? line.bold : line; if (isBold) doc.setFont('helvetica', 'bold'); doc.text(text, x + 4, boxY + 12 + i * 5); if (isBold) doc.setFont('helvetica', 'normal'); });
  };
  const billLines = [{ bold: college.name || 'College Name' }, `Admin: ${tx.purchased_by || '—'}`, `Email: ${college.email || '—'}`, `College ID: ${String(college.id || '').slice(0, 12)}`, `Plan: ${planLabel(tx.plan_code, tx.plan_name)}`];
  const coLines = [{ bold: 'EvolveX Innovations' }, `GSTIN: ${company.gstin}`, `Ph: +91 62815 97586`, `Email: ${company.email}`, `Web: ${company.website}`, 'Bhoomatha Thalluri Heights, Kankipadu,', 'Vijayawada - 521151'];
  const boxH = Math.max(12 + billLines.length * 5 + 2, 12 + coLines.length * 5 + 2);
  drawBox(margin, y, colW, boxH, 'Bill To', billLines); drawBox(margin + colW + 6, y, colW, boxH, 'Company Info', coLines); y += boxH + 8;
  const base = parseFloat(tx.amount_inr || 0), gst = parseFloat(tx.gst_amount || base * 0.18), total = parseFloat(tx.total_paid || base + gst), qty = tx.credits_amount || 0, unitPrice = qty > 0 ? (base / qty) : parseFloat(tx.effective_price_per_credit || 0);
  const tblCols = ['Item Description', 'Qty (Credits)', 'Unit Price (Rs.)', 'Amount (Rs.)'], tblWidths = [contentW * 0.45, contentW * 0.18, contentW * 0.18, contentW * 0.19], tblRowH = 7;
  setFill(...BRAND); doc.rect(margin, y, contentW, tblRowH, 'F'); setRGB(255, 255, 255); doc.setFontSize(7); doc.setFont('helvetica', 'bold');
  let cx2 = margin; tblCols.forEach((col, i) => { doc.text(col, cx2 + 3, y + 4.5); cx2 += tblWidths[i]; }); y += tblRowH;
  setFill(255, 255, 255); doc.rect(margin, y, contentW, tblRowH, 'F'); setRGB(...DARK); doc.setFont('helvetica', 'normal');
  const dataRow = [planLabel(tx.plan_code, tx.plan_name) + ' Credits', String(qty), `Rs.${fmt(unitPrice.toFixed(0))}`, `Rs.${fmt(base.toFixed(0))}`];
  cx2 = margin; dataRow.forEach((val, i) => { const align = i > 0 ? 'right' : 'left'; doc.text(val, i > 0 ? cx2 + tblWidths[i] - 3 : cx2 + 3, y + 4.5, { align }); cx2 += tblWidths[i]; });
  setDraw(229, 231, 235); doc.setLineWidth(0.2); doc.rect(margin, y - tblRowH, contentW, tblRowH * 2, 'D'); y += tblRowH + 6;
  const taxX = margin + contentW - 80, taxW = 80;
  setFill(...BG); setDraw(229, 231, 235); doc.setLineWidth(0.3); doc.roundedRect(taxX, y, taxW, 22, 2, 2, 'FD');
  setRGB(...BRAND); doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.text('TAX SUMMARY', taxX + 3, y + 5); doc.line(taxX + 3, y + 6.5, taxX + taxW - 3, y + 6.5);
  [['Subtotal', `Rs.${fmt(base.toFixed(0))}`], ['GST @ 18%', `Rs.${fmt(gst.toFixed(0))}`]].forEach(([l, v], i) => { const ry = y + 10 + i * 6; setRGB(...MID); doc.setFont('helvetica', 'normal'); doc.text(l, taxX + 3, ry); setRGB(...DARK); doc.text(v, taxX + taxW - 3, ry, { align: 'right' }); }); y += 30;
  setFill(...BRAND); doc.roundedRect(margin, y, contentW, 18, 3, 3, 'F'); setRGB(255, 255, 255); doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.text('TOTAL AMOUNT PAID', margin + 8, y + 8); doc.setFontSize(12); doc.text(`Rs.${fmt(total.toFixed(0))}`, W - margin - 6, y + 11, { align: 'right' }); y += 24;
  setFill(243, 244, 246); doc.rect(0, H - 22, W, 22, 'F'); setRGB(...MID); doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.text('This is a computer-generated receipt and does not require a signature.', W / 2, H - 17, { align: 'center' }); doc.setFont('helvetica', 'bold'); setRGB(...BRAND); doc.text('Powered by EvolveX Innovations Pvt. Ltd.', W / 2, H - 12, { align: 'center' }); setRGB(...MID); doc.setFont('helvetica', 'normal'); doc.text(`${company.email}  |  +91 62815 97586  |  ${company.website}`, W / 2, H - 7, { align: 'center' });
  const txId = tx.razorpay_payment_id || tx.razorpay_order_id || 'receipt';
  doc.save(`Receipt_${txId}.pdf`);
}

/* ── Enterprise Detail Modal ───────────────────────────────────── */
const EnterpriseDetailModal = ({ purchase, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const collegeId = user.id || user.userId;

  useEffect(() => {
    if (!purchase) return;
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/credits/enterprise/purchases/${purchase.id}?college_id=${collegeId}`
        );
        if (res.data.success) {
          setDetail(res.data);
        } else {
          setError(res.data.error || 'Failed to load details.');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load details.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [purchase?.id, collegeId]);

  if (!purchase) return null;

  const ep = detail?.purchase || purchase;
  const audit = detail?.audit || [];

  const AUDIT_ICONS = {
    QUOTATION_GENERATED:   { bg: '#EFF6FF', icon: '📋' },
    QUOTATION_DOWNLOADED:  { bg: '#EFF6FF', icon: '📥' },
    UTR_SUBMITTED:         { bg: '#FFFBEB', icon: '💳' },
    PAYMENT_RESUBMITTED:   { bg: '#F0F9FF', icon: '🔄' },
    ADMIN_APPROVED:        { bg: '#F0FDF4', icon: '✅' },
    ADMIN_REJECTED:        { bg: '#FEF2F2', icon: '❌' },
    CLARIFICATION_REQUESTED: { bg: '#FFF7ED', icon: 'ℹ️' },
    ADMIN_VIEWED:          { bg: '#F8FAFC', icon: '👁️' },
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '680px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(15,23,42,0.20)' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg,#7C3AED,#4C1D95)', borderRadius: '20px 20px 0 0' }}>
          <div>
            <p style={{ fontSize: '15px', fontWeight: 800, color: '#fff', margin: 0 }}>Enterprise Payment Details</p>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', margin: '2px 0 0' }}>{ep.quotation_number}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <EntStatusBadge status={ep.status} />
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: '#fff', padding: '6px', borderRadius: '8px', display: 'flex' }}>
              <X style={{ width: '16px', height: '16px' }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <RefreshCw style={{ width: '24px', height: '24px', color: '#7C3AED', margin: '0 auto 12px', display: 'block', animation: 'spin 1s linear infinite' }} />
              <p style={{ fontSize: '13px', color: '#64748B' }}>Loading details...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <AlertCircle style={{ width: '24px', height: '24px', color: '#DC2626', margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: '13px', color: '#DC2626' }}>{error}</p>
            </div>
          ) : (
            <>
              {/* Quotation Info */}
              <section style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Quotation Information</p>
                <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '14px 16px' }}>
                  {[
                    { label: 'Quotation Number', value: ep.quotation_number },
                    { label: 'Plan', value: planLabel(ep.plan_code, ep.plan_name) },
                    { label: 'Credits', value: `${(ep.credits_purchased || 0).toLocaleString('en-IN')} credits` },
                    { label: 'Subtotal', value: fmtINR(ep.subtotal) },
                    { label: 'GST @ 18%', value: fmtINR(ep.gst) },
                    { label: 'Grand Total', value: fmtINR(ep.grand_total), bold: true, accent: '#7C3AED' },
                  ].map(({ label, value, bold, accent }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: bold ? '2px solid #E2E8F0' : '1px dashed #E2E8F0', marginTop: bold ? '4px' : 0 }}>
                      <span style={{ fontSize: '12px', color: '#64748B', fontWeight: bold ? 700 : 500 }}>{label}</span>
                      <span style={{ fontSize: bold ? '16px' : '13px', fontWeight: bold ? 800 : 600, color: accent || '#0F172A' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Payment Info */}
              <section style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Payment Information</p>
                <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '14px 16px' }}>
                  {[
                    { label: 'UTR Number', value: ep.utr_number ? <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0F172A' }}>{ep.utr_number}</span> : <span style={{ color: '#94A3B8' }}>Not submitted yet</span> },
                    { label: 'Submitted Date', value: fmtDate(ep.submitted_at) },
                    { label: 'Submitted By', value: ep.submitted_by || '—' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F1F5F9' }}>
                      <span style={{ fontSize: '12px', color: '#64748B', fontWeight: 500 }}>{label}</span>
                      <span style={{ fontSize: '13px', fontWeight: 500 }}>{value}</span>
                    </div>
                  ))}
                  {ep.remarks && (
                    <div style={{ marginTop: '10px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <p style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 600, margin: '0 0 4px' }}>REMARKS</p>
                      <p style={{ fontSize: '12px', color: '#475569', margin: 0, lineHeight: 1.5 }}>{ep.remarks}</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Attachments */}
              {(ep.payment_proof_path || ep.purchase_order_path) && (
                <section style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Attachments</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {ep.payment_proof_path && (
                      <a href={ep.payment_proof_path} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', textDecoration: 'none', color: '#1E40AF' }}>
                        <FileText style={{ width: '14px', height: '14px' }} />
                        <span style={{ fontSize: '12px', fontWeight: 600, flex: 1 }}>Payment Proof</span>
                        <Download style={{ width: '13px', height: '13px' }} />
                      </a>
                    )}
                    {ep.purchase_order_path && (
                      <a href={ep.purchase_order_path} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', textDecoration: 'none', color: '#1E40AF' }}>
                        <FileText style={{ width: '14px', height: '14px' }} />
                        <span style={{ fontSize: '12px', fontWeight: 600, flex: 1 }}>Purchase Order</span>
                        <Download style={{ width: '13px', height: '13px' }} />
                      </a>
                    )}
                  </div>
                </section>
              )}

              {/* Rejection / Clarification */}
              {ep.rejection_reason && (
                <section style={{ marginBottom: '20px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px', padding: '14px 16px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#7F1D1D', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>Rejection Reason</p>
                  <p style={{ fontSize: '13px', color: '#991B1B', margin: 0, lineHeight: 1.5 }}>{ep.rejection_reason}</p>
                </section>
              )}
              {ep.clarification_message && (() => {
                const clarifyEvent = [...audit].reverse().find(a => a.action === 'CLARIFICATION_REQUESTED');
                return (
                  <section style={{ marginBottom: '20px', background: '#FFF7ED', border: '2px solid #FED7AA', borderRadius: '14px', overflow: 'hidden' }}>
                    {/* Action Required header */}
                    <div style={{ background: 'linear-gradient(135deg,#EA580C,#C2410C)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AlertCircle style={{ width: '14px', height: '14px', color: '#fff', flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Action Required — Clarification Needed</span>
                    </div>
                    <div style={{ padding: '14px 16px' }}>
                      {clarifyEvent && (
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px dashed #FED7AA' }}>
                          <div>
                            <p style={{ fontSize: '10px', color: '#92400E', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 2px' }}>Requested By</p>
                            <p style={{ fontSize: '12px', color: '#7C2D12', fontWeight: 600, margin: 0 }}>{clarifyEvent.performed_by}</p>
                          </div>
                          <div>
                            <p style={{ fontSize: '10px', color: '#92400E', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 2px' }}>Requested On</p>
                            <p style={{ fontSize: '12px', color: '#7C2D12', fontWeight: 600, margin: 0 }}>{fmtDate(clarifyEvent.created_at)}</p>
                          </div>
                        </div>
                      )}
                      <p style={{ fontSize: '11px', fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Message from Finance Team</p>
                      <p style={{ fontSize: '13px', color: '#7C2D12', margin: 0, lineHeight: 1.6, fontWeight: 500 }}>{ep.clarification_message}</p>
                      <p style={{ fontSize: '11px', color: '#92400E', fontWeight: 600, margin: '10px 0 0' }}>
                        Please update your UTR number, upload new payment proof or documents, add a response, then click "Update &amp; Resubmit" below.
                      </p>
                    </div>
                  </section>
                );
              })()}
              {ep.invoice_number && (
                <section style={{ marginBottom: '20px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px', padding: '14px 16px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#065F46', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Invoice Generated</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '12px', color: '#15803D' }}>Invoice Number</span><span style={{ fontSize: '13px', fontWeight: 700, color: '#14532D' }}>{ep.invoice_number}</span></div>
                  {ep.verified_by && <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}><span style={{ fontSize: '12px', color: '#15803D' }}>Approved By</span><span style={{ fontSize: '12px', fontWeight: 600, color: '#14532D' }}>{ep.verified_by}</span></div>}
                  {ep.activated_at && <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}><span style={{ fontSize: '12px', color: '#15803D' }}>Activated At</span><span style={{ fontSize: '12px', color: '#14532D' }}>{fmtDate(ep.activated_at)}</span></div>}
                </section>
              )}

              {/* Status Timeline */}
              {audit.length > 0 && (
                <section style={{ marginBottom: '8px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>Status Timeline</p>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {audit.map((entry, i) => {
                      const cfg = AUDIT_ICONS[entry.action] || { bg: '#F8FAFC', icon: '•' };
                      return (
                        <div key={entry.id || i} style={{ display: 'flex', gap: '10px', paddingBottom: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', border: '2px solid #fff', boxShadow: '0 0 0 1px #E2E8F0' }}>{cfg.icon}</div>
                            {i < audit.length - 1 && <div style={{ width: '1px', flex: 1, background: '#E2E8F0', margin: '3px 0' }} />}
                          </div>
                          <div style={{ flex: 1, paddingTop: '4px' }}>
                            <p style={{ fontSize: '12px', fontWeight: 700, color: '#0F172A', margin: '0 0 2px' }}>{entry.action.replace(/_/g, ' ')}</p>
                            {entry.description && <p style={{ fontSize: '11px', color: '#64748B', margin: '0 0 2px', lineHeight: 1.4 }}>{entry.description}</p>}
                            <p style={{ fontSize: '10px', color: '#94A3B8', margin: 0 }}>{entry.performed_by} · {fmtDate(entry.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          {ep.status === 'CLARIFICATION_REQUESTED' && (
            <button
              onClick={() => { onClose(); navigate(`/college/enterprise-purchase/${ep.id}`); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                padding: '10px 18px', borderRadius: '10px', border: 'none',
                background: 'linear-gradient(135deg,#EA580C,#C2410C)',
                fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer',
                boxShadow: '0 3px 10px rgba(234,88,12,0.30)',
              }}
            >
              <MessageSquare style={{ width: '14px', height: '14px' }} />
              Update &amp; Resubmit
            </button>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto', padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #E2E8F0', background: '#fff', fontSize: '13px', fontWeight: 600, color: '#475569', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   Enterprise Payments Tab
════════════════════════════════════════════════════════════════ */
const EnterprisePaymentsTab = ({ collegeId }) => {
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const navigate = useNavigate();
  const LIMIT = 15;

  const fetchPurchases = useCallback(async (p = 1) => {
    if (!collegeId) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ college_id: collegeId, page: String(p), limit: String(LIMIT) });
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`${API_BASE_URL}/credits/enterprise/purchases?${params}`);
      if (res.data.success) {
        setPurchases(res.data.purchases || []);
        setPagination(res.data.pagination || { total: 0, totalPages: 1 });
        // Compute simple stats from the data
        const all = res.data.purchases || [];
        setStats({
          total: res.data.pagination?.total || all.length,
          approved: all.filter(p => p.status === 'APPROVED').length,
          pending: all.filter(p => ['PENDING','SUBMITTED','UNDER_VERIFICATION','RESUBMITTED'].includes(p.status)).length,
          rejected: all.filter(p => p.status === 'REJECTED').length,
        });
      } else {
        setError(res.data.error || 'Failed to load enterprise payments.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load enterprise payments. Please try again.');
    } finally { setLoading(false); }
  }, [collegeId, statusFilter]);

  useEffect(() => { fetchPurchases(1); setPage(1); }, [fetchPurchases, statusFilter]);

  const filtered = search
    ? purchases.filter(p => (p.quotation_number || '').toLowerCase().includes(search.toLowerCase()) || (p.utr_number || '').toLowerCase().includes(search.toLowerCase()) || (p.plan_name || '').toLowerCase().includes(search.toLowerCase()))
    : purchases;

  const summaryCards = stats ? [
    { label: 'Total Payments',  value: stats.total,    color: '#4F46E5', bg: '#EEF2FF' },
    { label: 'Pending Review',  value: stats.pending,  color: '#D97706', bg: '#FFFBEB' },
    { label: 'Approved',        value: stats.approved, color: '#059669', bg: '#F0FDF4' },
    { label: 'Rejected',        value: stats.rejected, color: '#DC2626', bg: '#FEF2F2' },
  ] : [];

  return (
    <div>
      {/* Summary cards */}
      {!loading && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {summaryCards.map(({ label, value, color, bg }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
                <Building2 size={16} style={{ color }} />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider leading-tight">{label}</p>
                <p className="text-xl font-black text-gray-900">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text" placeholder="Search quotation #, UTR, plan..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 bg-gray-50"
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-400 min-w-[160px]">
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="UNDER_VERIFICATION">Under Verification</option>
            <option value="RESUBMITTED">Resubmitted</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="CLARIFICATION_REQUESTED">Clarification Needed</option>
          </select>
          <button onClick={() => fetchPurchases(page)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          {(search || statusFilter) && (
            <button onClick={() => { setSearch(''); setStatusFilter(''); }}
              className="flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700 px-2 py-2">
              <X size={13} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <AlertCircle size={28} className="text-red-400" />
            <p className="font-bold text-gray-700">Failed to load enterprise payments</p>
            <p className="text-sm text-gray-400">{error}</p>
            <button onClick={() => fetchPurchases(page)} className="px-6 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition">Retry</button>
          </div>
        ) : loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center">
              <Building2 size={32} className="text-purple-300" />
            </div>
            <p className="font-black text-gray-600">No Enterprise Payments Found</p>
            <p className="text-sm text-gray-400">
              {statusFilter || search ? 'Try adjusting your filters.' : 'Enterprise payment records will appear here after you submit payment for a quotation above ₹5,00,000.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Quotation #', 'Plan', 'Credits', 'Grand Total', 'UTR Number', 'Submitted Date', 'Status', 'Actions'].map(col => (
                      <th key={col} className="px-4 py-3 text-left text-[10px] font-black text-gray-500 uppercase tracking-wider whitespace-nowrap first:pl-5 last:pr-5">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((ep, i) => (
                    <tr key={ep.id} style={ep.status === 'CLARIFICATION_REQUESTED' ? { background: '#FFF7ED', borderLeft: '3px solid #EA580C' } : {}} className={ep.status === 'CLARIFICATION_REQUESTED' ? 'transition-colors' : `hover:bg-indigo-50/30 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                      <td className="pl-5 pr-3 py-3.5 whitespace-nowrap">
                        <span className="text-xs font-mono font-bold text-indigo-700">{ep.quotation_number}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="text-xs font-bold text-gray-800">{planLabel(ep.plan_code, ep.plan_name)}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-black text-indigo-700">{(ep.credits_purchased || 0).toLocaleString('en-IN')}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-black" style={{ color: '#4F46E5' }}>{fmtINR(ep.grand_total)}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {ep.utr_number
                          ? <span className="text-xs font-mono font-bold bg-gray-100 px-2 py-0.5 rounded">{ep.utr_number}</span>
                          : <span className="text-[10px] text-gray-400 font-medium">Not submitted</span>
                        }
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="text-xs text-gray-600">{ep.submitted_at ? fmtDate(ep.submitted_at) : <span className="text-gray-400">—</span>}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <EntStatusBadge status={ep.status} />
                          {ep.status === 'CLARIFICATION_REQUESTED' && (
                            <span style={{ fontSize: '9px', fontWeight: 800, color: '#EA580C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>⚠ Action Required</span>
                          )}
                        </div>
                      </td>
                      <td className="pr-5 pl-4 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {ep.status === 'CLARIFICATION_REQUESTED' && (
                            <button
                              onClick={() => navigate(`/college/enterprise-purchase/${ep.id}`)}
                              style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#EA580C,#C2410C)', fontSize: '11px', fontWeight: 700, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(234,88,12,0.30)' }}
                            >
                              <MessageSquare size={11} /> Update &amp; Resubmit
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedPurchase(ep)}
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 bg-white text-xs font-bold text-gray-600 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-all"
                          >
                            <Eye size={12} /> View Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > LIMIT && (
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500 font-medium">
                  Showing <span className="font-bold text-gray-700">{((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, pagination.total)}</span> of <span className="font-bold text-gray-700">{pagination.total}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setPage(p => Math.max(1, p - 1)); fetchPurchases(Math.max(1, page - 1)); }} disabled={page === 1}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <ChevronLeft size={15} />
                  </button>
                  <span className="text-xs font-bold text-gray-700">{page} / {pagination.totalPages}</span>
                  <button onClick={() => { setPage(p => Math.min(pagination.totalPages, p + 1)); fetchPurchases(Math.min(pagination.totalPages, page + 1)); }} disabled={page === pagination.totalPages}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail modal */}
      {selectedPurchase && <EnterpriseDetailModal purchase={selectedPurchase} onClose={() => setSelectedPurchase(null)} />}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   Online Payments Tab (existing Razorpay transaction table)
════════════════════════════════════════════════════════════════ */
const OnlinePaymentsTab = ({ collegeId }) => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const LIMIT = 15;

  const fetchTransactions = useCallback(async (p = page) => {
    if (!collegeId) { setError('College ID not found. Please log in again.'); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ college_id: collegeId, page: String(p), limit: String(LIMIT), ...(search ? { search } : {}), ...(status ? { status } : {}), ...(fromDate ? { fromDate } : {}), ...(toDate ? { toDate } : {}) });
      const res = await axios.get(`${API_BASE_URL}/credits/purchase-transactions?${params}`);
      setTransactions(res.data.transactions || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1 });
      setStats(res.data.stats || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load transactions. Please try again.');
    } finally { setLoading(false); }
  }, [collegeId, page, search, status, fromDate, toDate]);

  useEffect(() => { fetchTransactions(1); setPage(1); }, [search, status, fromDate, toDate]);
  useEffect(() => { fetchTransactions(page); }, [page]);

  const handleDownloadReceipt = async (tx) => {
    const txId = tx.razorpay_payment_id || tx.razorpay_order_id;
    setDownloading(txId);
    try {
      const res = await axios.get(`${API_BASE_URL}/credits/purchase-transactions/${txId}/receipt-data?college_id=${collegeId}`);
      await generateReceiptPDF(res.data.receipt);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate receipt. Please try again.');
    } finally { setDownloading(null); }
  };

  const clearFilters = () => { setSearch(''); setStatus(''); setFromDate(''); setToDate(''); setPage(1); };
  const hasFilters = search || status || fromDate || toDate;

  const summaryCards = stats ? [
    { label: 'Total Transactions', value: stats.total_count || 0,    icon: <Hash size={18}/>,         color: '#4F46E5', bg: '#EEF2FF' },
    { label: 'Total Credits',      value: fmt(stats.total_credits),   icon: <CreditCard size={18}/>,   color: '#7C3AED', bg: '#F5F3FF' },
    { label: 'Total Amount Paid',  value: `₹${fmt(parseFloat(stats.total_amount_paid || 0).toFixed(0))}`, icon: <IndianRupee size={18}/>, color: '#059669', bg: '#F0FDF4' },
    { label: 'Successful',         value: stats.success_count || 0,   icon: <CheckCircle2 size={18}/>, color: '#059669', bg: '#F0FDF4' },
    { label: 'Failed',             value: stats.failed_count || 0,    icon: <XCircle size={18}/>,      color: '#DC2626', bg: '#FEF2F2' },
  ] : [];

  return (
    <div>
      {loading && !stats ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : stats ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {summaryCards.map(({ label, value, icon, color, bg }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: bg, color }}>{icon}</div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider leading-tight truncate">{label}</p>
                <p className="text-lg font-black text-gray-900 leading-tight">{value}</p>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input type="text" placeholder="Search Transaction ID or Order ID…" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 bg-gray-50" />
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)} className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-400 min-w-[140px]">
            <option value="">All Statuses</option><option value="SUCCESS">Success</option><option value="PENDING">Pending</option><option value="FAILED">Failed</option>
          </select>
          <div className="flex flex-col"><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">From</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-400" /></div>
          <div className="flex flex-col"><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">To</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-400" /></div>
          <button onClick={() => fetchTransactions(page)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          {hasFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700 px-2 py-2"><X size={13} /> Clear</button>}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <AlertCircle size={28} className="text-red-400" />
            <p className="font-bold text-gray-700">Failed to load transactions</p>
            <p className="text-sm text-gray-400">{error}</p>
            <button onClick={() => fetchTransactions(page)} className="px-6 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition">Retry</button>
          </div>
        ) : loading ? (
          <div className="p-4 space-y-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center"><Receipt size={32} className="text-indigo-300" /></div>
            <p className="font-black text-gray-600">No Online Transactions Found</p>
            <p className="text-sm text-gray-400">{hasFilters ? 'Try adjusting your search or filters.' : 'Online credit purchases via Razorpay will appear here.'}</p>
            {hasFilters && <button onClick={clearFilters} className="text-sm font-bold text-indigo-600 hover:underline">Clear Filters</button>}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-100">
                  {['Transaction ID','Order ID','Purchase Date','Plan','Credits','Amount','GST (18%)','Total Paid','Payment Method','Status','Receipt'].map(col => (
                    <th key={col} className="px-4 py-3 text-left text-[10px] font-black text-gray-500 uppercase tracking-wider whitespace-nowrap first:pl-5 last:pr-5">{col}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {transactions.map((tx, i) => {
                    const meta = statusMeta(tx.payment_status);
                    const txId = tx.razorpay_payment_id || tx.razorpay_order_id || '—';
                    const isSuccess = (tx.payment_status || '').toUpperCase() === 'SUCCESS';
                    const isDownloading = downloading === (tx.razorpay_payment_id || tx.razorpay_order_id);
                    return (
                      <tr key={tx.id || i} className={`hover:bg-indigo-50/30 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                        <td className="pl-5 pr-3 py-3.5 min-w-[150px]"><span className="text-xs font-mono font-bold text-indigo-700 truncate block max-w-[140px]" title={txId}>{txId !== '—' ? txId.slice(-12) : '—'}</span></td>
                        <td className="px-4 py-3.5 min-w-[150px]"><span className="text-xs font-mono text-gray-600 truncate block max-w-[140px]" title={tx.razorpay_order_id}>{(tx.razorpay_order_id || '—').slice(-12)}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap"><span className="text-xs text-gray-700">{fmtDate(tx.created_at)}</span></td>
                        <td className="px-4 py-3.5 min-w-[130px]"><span className="text-xs font-bold text-gray-800">{planLabel(tx.plan_code, tx.plan_name)}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-right"><span className="text-sm font-black text-indigo-700">{fmt(tx.credits_amount)}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-right"><span className="text-sm font-bold text-gray-700">₹{fmt(parseFloat(tx.amount_inr || 0).toFixed(0))}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-right"><span className="text-sm text-gray-500">₹{fmt(parseFloat(tx.gst_amount || 0).toFixed(0))}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-right"><span className="text-sm font-black text-gray-900">₹{fmt(parseFloat(tx.total_paid || 0).toFixed(0))}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap"><span className="text-xs text-gray-600">{tx.payment_method || 'Online'}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black border ${meta.cls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>{meta.label}
                          </div>
                        </td>
                        <td className="pr-5 pl-4 py-3.5 whitespace-nowrap">
                          {isSuccess ? (
                            <button onClick={() => handleDownloadReceipt(tx)} disabled={isDownloading} title="Download PDF Receipt"
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-all disabled:opacity-60 disabled:cursor-wait shadow-sm shadow-indigo-200">
                              {isDownloading ? <><div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin"/>Generating…</> : <><Download size={12}/>PDF</>}
                            </button>
                          ) : <span className="text-[10px] text-gray-300 font-bold">N/A</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pagination.total > 0 && (
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500 font-medium">Showing <span className="font-bold text-gray-700">{((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, pagination.total)}</span> of <span className="font-bold text-gray-700">{pagination.total}</span></p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"><ChevronLeft size={15} /></button>
                  <span className="text-xs font-bold text-gray-700">{page} / {pagination.totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page === pagination.totalPages} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"><ChevronRight size={15} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   Main Component
════════════════════════════════════════════════════════════════ */
const CollegeTransactionHistory = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const collegeId = user.id || user.userId;
  const [activeTab, setActiveTab] = useState('enterprise');

  const tabs = [
    { id: 'enterprise', label: 'Enterprise Payments', icon: <Building2 size={15} />, desc: 'Bank transfer payments' },
    { id: 'online',     label: 'Online Payments',     icon: <CreditCard size={15} />, desc: 'Razorpay transactions' },
  ];

  return (
    <div className="college-main-layout">
      <CollegeHeader />
      <div className="college-content-wrapper">
        <CollegeHomeSideBar />
        <main className="college-main-content">

          {/* Page Header */}
          <header className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl text-white shadow-lg shadow-indigo-200">
                <Receipt size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900">Transaction History</h1>
                <p className="text-sm text-gray-500">All payment transactions for your institution</p>
              </div>
            </div>
          </header>

          {/* Tab Switcher */}
          <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-2xl w-fit">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-indigo-700 shadow-sm shadow-indigo-100'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'enterprise' && <EnterprisePaymentsTab collegeId={collegeId} />}
          {activeTab === 'online' && <OnlinePaymentsTab collegeId={collegeId} />}

        </main>
      </div>
    </div>
  );
};

export default CollegeTransactionHistory;
